import React, { createContext, useContext, useEffect, useState } from 'react';
import { AuthUser } from '../services/authService';
import { getDashboard } from '../services/userService';
import { hasActiveSubscriptionStatus } from '../utils/subscription';

interface AuthContextType {
  isLoggedIn: boolean;
  hasSubscription: boolean;
  authLoading: boolean;
  currentUser: AuthUser | null;
  loginUser: (token: string, user?: AuthUser) => void;
  logoutUser: () => void;
  setSubscriptionStatus: (value: boolean) => void;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [currentUser, setCurrentUser] = useState<AuthUser | null>(() => {
    const storedUser = localStorage.getItem('authUser');
    if (!storedUser) return null;

    try {
      return JSON.parse(storedUser) as AuthUser;
    } catch {
      localStorage.removeItem('authUser');
      return null;
    }
  });
  const [hasSubscription, setHasSubscription] = useState(false);
  const [authLoading, setAuthLoading] = useState(Boolean(token));

  useEffect(() => {
    const hydrateSubscription = async () => {
      if (!token) {
        setHasSubscription(false);
        setAuthLoading(false);
        return;
      }

      setAuthLoading(true);
      try {
        const response = await getDashboard(token);
        setCurrentUser((existingUser) => {
          const dashboardName = `${response.data.firstname || ''} ${response.data.lastname || ''}`.trim();
          const hasFullExistingName = (existingUser?.name || '').trim().split(/\s+/).length > 1;
          const hasFullDashboardName = dashboardName.split(/\s+/).length > 1;
          const dashboardUser: AuthUser = {
            id: existingUser?.id || 0,
            name: hasFullExistingName && !hasFullDashboardName ? existingUser?.name || dashboardName : dashboardName,
            email: response.data.email,
            user_meta: existingUser?.user_meta,
          };
          localStorage.setItem('authUser', JSON.stringify(dashboardUser));
          return dashboardUser;
        });

        setHasSubscription(hasActiveSubscriptionStatus(response.data.subscriptions));
      } catch {
        setHasSubscription(false);
      } finally {
        setAuthLoading(false);
      }
    };

    hydrateSubscription();
  }, [token]);

  const loginUser = (t: string, user?: AuthUser) => {
    localStorage.setItem('token', t);
    if (user) {
      localStorage.setItem('authUser', JSON.stringify(user));
      setCurrentUser(user);
    }
    setToken(t);
  };

  const logoutUser = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('authUser');
    setToken(null);
    setCurrentUser(null);
    setHasSubscription(false);
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn: !!token,
        hasSubscription,
        authLoading,
        currentUser,
        loginUser,
        logoutUser,
        setSubscriptionStatus: setHasSubscription,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
